import React, { useMemo } from 'react';
import { Box, Chip, Link, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import ReportActionMenu from './ReportActionMenu';

const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const scopeColor = (scope) => {
  if (!scope) return 'default';
  const normalized = scope.toLowerCase();
  if (normalized === 'dpe') return 'primary';
  if (normalized === 'appsheet') return 'secondary';
  if (normalized === 'combined') return 'info';
  return 'default';
};

const statusColor = (status) => {
  if (status === 'archived') return 'default';
  if (status === 'active') return 'success';
  return 'warning';
};

const ReportsGrid = ({ rows, datasetMap, onArchive, onUnarchive, onEdit, onCopyLink }) => {
  const navigate = useNavigate();

  const columns = useMemo(() => (
    [
      {
        field: 'name',
        headerName: 'Name',
        minWidth: 220,
        flex: 1,
        renderCell: (params) => (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Link
              component={RouterLink}
              to={`/reports/${params?.row?.id}`}
              underline="hover"
              onClick={(event) => {
                event.stopPropagation();
                if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
                  return;
                }
                event.preventDefault();
                navigate(`/reports/${params.row.id}`);
              }}
              sx={{ fontWeight: 600 }}
            >
              {params.value}
            </Link>
            {params?.row?.description && (
              <Typography variant="caption" color="text.secondary">
                {params.row.description}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        field: 'scope',
        headerName: 'Scope',
        minWidth: 120,
        renderCell: (params) => (
          <Chip
            size="small"
            label={(params.value || 'N/A').toString().toUpperCase()}
            color={scopeColor(params.value)}
            variant="outlined"
          />
        ),
      },
      {
        field: 'baseDataset',
        headerName: 'Base Dataset',
        minWidth: 180,
        valueGetter: (params) =>
          datasetMap.get(params?.row?.baseDataset) || params?.row?.baseDataset || 'N/A',
      },
      {
        field: 'createdAt',
        headerName: 'Created Date',
        minWidth: 170,
        valueGetter: (params) => formatDateTime(params?.row?.createdAt),
      },
      {
        field: 'updatedAt',
        headerName: 'Last Updated',
        minWidth: 170,
        valueGetter: (params) => formatDateTime(params?.row?.updatedAt),
      },
      {
        field: 'lastRunAt',
        headerName: 'Last Run',
        minWidth: 150,
        valueGetter: (params) => formatDateTime(params?.row?.lastRunAt),
      },
      {
        field: 'queryTimeMs',
        headerName: 'Query Time',
        minWidth: 120,
        valueGetter: (params) => (params?.row?.queryTimeMs ? `${params.row.queryTimeMs} ms` : 'N/A'),
      },
      {
        field: 'widgetsCount',
        headerName: 'Widgets',
        minWidth: 90,
        valueGetter: (params) =>
          Number.isFinite(params?.row?.widgetsCount) ? params.row.widgetsCount : 'N/A',
      },
      {
        field: 'status',
        headerName: 'Status',
        minWidth: 120,
        renderCell: (params) => (
          <Chip
            size="small"
            label={(params.value || 'unknown').toString()}
            color={statusColor(params.value)}
            variant={params.value === 'archived' ? 'outlined' : 'filled'}
          />
        ),
      },
      {
        field: 'actions',
        headerName: '',
        sortable: false,
        filterable: false,
        minWidth: 80,
        renderCell: (params) => (
          <ReportActionMenu
            report={params.row}
            onView={() => navigate(`/reports/${params.row.id}`)}
            onArchive={onArchive}
            onUnarchive={onUnarchive}
            onEdit={onEdit}
            onCopyLink={onCopyLink}
          />
        ),
      },
    ]
  ), [datasetMap, navigate, onArchive, onCopyLink, onEdit, onUnarchive]);

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle1" component="div" sx={{ mb: 1, fontWeight: 600 }}>
        Reports catalog
      </Typography>
      <DataGrid
        autoHeight
        rows={rows}
        columns={columns}
        pageSizeOptions={[10, 25, 50]}
        initialState={{
          sorting: { sortModel: [{ field: 'updatedAt', sort: 'desc' }] },
          pagination: { paginationModel: { pageSize: 25, page: 0 } },
        }}
        getRowId={(row) => row.id}
        disableRowSelectionOnClick
        onCellClick={(params) => {
          if (params.field === 'name' && params.row?.id) {
            navigate(`/reports/${params.row.id}`);
          }
        }}
      />
    </Box>
  );
};

export default ReportsGrid;
